// Leitura dos dados dos relatórios no Firestore, em tempo real onde o bot escreve
// (progresso e respostas chegam do WhatsApp e a tela se atualiza sozinha).
import { useEffect, useState } from 'react';
import { collection, doc, getCountFromServer, getDoc, getDocs, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { firestore } from '../../config/firebaseConfig';
import { variantesTelefone } from './util';

// Minicursos, progresso dos alunos e nomes dos contatos cadastrados do tenant.
export function useBaseRelatorio(tenantId) {
  const [minicursos, setMinicursos] = useState(null);
  const [progressos, setProgressos] = useState(null);
  const [nomesPorTelefone, setNomesPorTelefone] = useState({});
  const [erro, setErro] = useState('');
  const [atualizadoEm, setAtualizadoEm] = useState(null);

  useEffect(() => {
    if (!tenantId) return undefined;
    const falha = (e) => {
      console.error('Relatórios:', e);
      setErro('Não foi possível carregar os dados do relatório.');
    };

    const pararCursos = onSnapshot(
      query(collection(firestore, 'minicursos'), where('tenantId', '==', tenantId)),
      (s) => setMinicursos(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      falha,
    );
    const pararProgresso = onSnapshot(
      query(collection(firestore, 'progressoAlunos'), where('tenantId', '==', tenantId)),
      (s) => {
        setProgressos(s.docs.map((d) => ({ id: d.id, ...d.data() })));
        setAtualizadoEm(new Date());
      },
      falha,
    );
    getDocs(query(collection(firestore, 'contatos'), where('tenantId', '==', tenantId)))
      .then((s) => {
        const mapa = {};
        s.forEach((d) => {
          const c = d.data();
          if (!c.nome) return;
          variantesTelefone(c.telefone).forEach((tel) => { mapa[tel] = c.nome; });
          if (c.whatsappId) mapa[String(c.whatsappId).split('@')[0]] = c.nome;
        });
        setNomesPorTelefone(mapa);
      })
      .catch(falha);

    return () => {
      pararCursos();
      pararProgresso();
    };
  }, [tenantId]);

  return { minicursos, progressos, nomesPorTelefone, erro, atualizadoEm };
}

// Etapas do minicurso, em ordem, com o conteúdo (pílula ou questão) de cada uma.
export function useEtapasDoCurso(minicursoId) {
  const [etapas, setEtapas] = useState(null);

  useEffect(() => {
    if (!minicursoId) return undefined;
    let ativo = true;
    setEtapas(null);
    (async () => {
      const s = await getDocs(query(collection(firestore, 'etapasMinicurso'), where('minicursoId', '==', minicursoId), orderBy('ordem')));
      const lista = await Promise.all(s.docs.map(async (d) => {
        const etapa = d.data();
        const colecao = etapa.tipoConteudo === 'pilula' ? 'pilulas' : 'questoes';
        const conteudo = await getDoc(doc(firestore, colecao, etapa.conteudoId));
        return { id: d.id, ...etapa, conteudo: conteudo.exists() ? { id: conteudo.id, ...conteudo.data() } : null };
      }));
      if (ativo) setEtapas(lista);
    })().catch((e) => {
      console.error('Etapas do minicurso:', e);
      if (ativo) setEtapas([]);
    });
    return () => { ativo = false; };
  }, [minicursoId]);

  return etapas;
}

// Respostas do tenant filtradas por um campo (minicursoId ou telefone), em tempo real.
export function useRespostas(tenantId, campo, valor) {
  const [respostas, setRespostas] = useState(null);

  useEffect(() => {
    if (!tenantId || !valor) return undefined;
    setRespostas(null);
    return onSnapshot(
      query(collection(firestore, 'respostas'), where('tenantId', '==', tenantId), where(campo, '==', valor)),
      (s) => setRespostas(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => {
        console.error('Respostas:', e);
        setRespostas([]);
      },
    );
  }, [tenantId, campo, valor]);

  return respostas;
}

// Quantidade de etapas de cada minicurso (para "etapa X de N" na visão do aluno).
export function useTotaisDeEtapas(minicursoIds) {
  const [totais, setTotais] = useState({});
  const chave = [...new Set(minicursoIds)].sort().join(',');

  useEffect(() => {
    if (!chave) return undefined;
    let ativo = true;
    Promise.all(chave.split(',').map(async (id) => {
      const s = await getCountFromServer(query(collection(firestore, 'etapasMinicurso'), where('minicursoId', '==', id)));
      return [id, s.data().count];
    }))
      .then((pares) => { if (ativo) setTotais(Object.fromEntries(pares)); })
      .catch((e) => console.error('Total de etapas:', e));
    return () => { ativo = false; };
  }, [chave]);

  return totais;
}
